import { Add, Edit, Settings, TableChart, TableRows, Visibility } from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { StatusOption } from '@sharedInterfaces/ICompany';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import getOpportunities from '@src/APIs/graphQl/Opportunity/getOpportunities';
import { Container } from '@src/App/Components/Core/Layouts/LayoutElements/Container/Container';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import type { IDataTableField } from '@src/App/Components/Tables/DataTable/DataTable';
import EntitiesPage, {
    EViewType,
    type IEntitiesPageAction,
} from '@src/App/Pages/EntityPages/Generic/EntitiesPage/EntitiesPage';
import type { IViewType } from '@src/App/Pages/EntityPages/Generic/EntitiesPage/ViewTypeMenu';
import { textContainsLowerCase } from '@src/Objects/Helper';
import type Opportunity from '@src/Objects/Opportunity';
import type { AppState } from '@store/store';
import { ClipboardList, ClipboardPlus, User, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MvpOpportunityCard from './MvpOpportunityCard';
import MvpOpportunityDialog from './MvpOpportunityDialog';
import MvpOpportunitySettingsDialog from './MvpOpportunitySettingsDialog';

/**
 * Modern MVP OpportunitiesPage using EntitiesPage architecture
 */
function MvpOpportunitiesPage() {
    const { openDialog } = useDialog();
    const navigate = useNavigate();
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).opportunities;
    const permissions = useSelector((state: AppState) => state.permissions);
    const entities = useSelector((state: AppState) => state.opportunity);
    const statusOptions = useSelector((state: AppState) => state.company.opportunitySettings.statusOptions);
    const statusOptionsMap = new Map<string, StatusOption>(statusOptions.map((option) => [option.id, option]));
    const emp = useSelector((state: AppState) => state.employee);

    const [selected, setSelected] = useState<number[]>([]);
    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);
    const [showScrollHint, setShowScrollHint] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [editingOpportunity, setEditingOpportunity] = useState<Opportunity | undefined>();
    const [activeRole, setActiveRole] = useState('manager');
    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        getOpportunities().catch((ex) => {
            setLoadingError(ex);
        });
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (tableContainerRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
                setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
            }
        };

        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            handleScroll(); // Initial check
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [tableContainerRef.current]);

    // Status Chip Component
    const StatusChip = ({ status }: { status: string }) => {
        const statusOption = statusOptionsMap.get(status);
        return (
            <Chip
                label={statusOption?.title || 'Unbekannt'}
                color={statusOption?.isActive ? 'success' : 'default'}
                variant="outlined"
                size="small"
                sx={{
                    fontWeight: 500,
                    minWidth: 80,
                }}
            />
        );
    };

    const tableFields: IDataTableField<any>[] = [
        { id: 'id', label: 'ID', align: 'center', disablePadding: false, type: 'string' },
        { id: 'externalId', label: langStrings.externalID, align: 'center', disablePadding: false, type: 'string' },
        { id: 'source', label: langStrings.sourceName, align: 'left', disablePadding: false, type: 'JSX.Element' },
        { id: 'title', label: langStrings.title, align: 'left', disablePadding: false, type: 'JSX.Element' },
        { id: 'customer', label: langStrings.customer, align: 'center', disablePadding: false, type: 'string' },
        { id: 'employees', label: langStrings.employees, align: 'center', disablePadding: false, type: 'string' },
        { id: 'projectStart', label: langStrings.startDate, align: 'center', disablePadding: false, type: 'date' },
        { id: 'projectEnd', label: langStrings.endDate, align: 'center', disablePadding: false, type: 'date' },
        { id: 'publishDate', label: langStrings.publishDate, align: 'center', disablePadding: false, type: 'date' },
        { id: 'deadline', label: langStrings.deadline, align: 'center', disablePadding: false, type: 'date' },
        {
            id: 'skillCoverage',
            label: langStrings.skillCoverage,
            align: 'center',
            disablePadding: false,
            type: 'percent',
        },
        { id: 'skillScore', label: langStrings.skillScore, align: 'center', disablePadding: false, type: 'percent' },
        { id: 'utilization', label: langStrings.utilization, align: 'center', disablePadding: false, type: 'percent' },
        { id: 'remote', label: langStrings.remote, align: 'center', disablePadding: false, type: 'percent' },
        { id: 'dayPrice', label: langStrings.dayPrice, align: 'center', disablePadding: false, type: 'currency' },
        { id: 'status', label: langStrings.status, align: 'center', disablePadding: false, type: 'JSX.Element' },
        {
            id: 'primaryRole',
            label: langStrings.primaryRole,
            align: 'center',
            disablePadding: false,
            type: 'JSX.Element',
        },
        { id: 'topSkills', label: langStrings.topSkills, align: 'center', disablePadding: false, type: 'string' },
    ];

    const types: IViewType<Opportunity>[] = [
        {
            id: EViewType.CARD,
            title: langStrings.oftenUsed.cards,
            icon: <TableChart fontSize="small" />,
            renderContainer: (allEntities, innerEntries) => (
                <Container>
                    {innerEntries.map((entity) => (
                        <MvpOpportunityCard key={entity.id} opportunity={entity} statusOptionsMap={statusOptionsMap} />
                    ))}
                </Container>
            ),
        },
        {
            id: EViewType.TABLE,
            title: langStrings.oftenUsed.table,
            icon: <TableRows fontSize="small" />,
            minWidth: 375,
            renderContainer: (allEntities, innerEntries, width, selectedItems, tableSettings) => {
                if (!tableSettings) return <div>Loading...</div>;

                const hiddenFieldsSet = new Set(
                    Object.keys(tableSettings.hiddenFields).filter((key) => tableSettings.hiddenFields[key]),
                );
                const visibleFields = tableFields.filter((f) => !hiddenFieldsSet.has(f.id));
                const sortedFields = visibleFields.sort((a, b) => {
                    const orderA = tableSettings.order.indexOf(a.id);
                    const orderB = tableSettings.order.indexOf(b.id);
                    return orderA - orderB;
                });

                return (
                    <Box sx={{ width: '100%', position: 'relative' }}>
                        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <TableContainer
                                ref={tableContainerRef}
                                sx={{
                                    overflowX: 'auto',
                                    '&::-webkit-scrollbar': {
                                        height: 8,
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: alpha(theme.palette.grey[300], 0.3),
                                        borderRadius: 4,
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.5),
                                        borderRadius: 4,
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.7),
                                        },
                                    },
                                }}
                            >
                                <Table>
                                    <TableHead
                                        sx={{
                                            bgcolor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.primary.light, 0.15)
                                                    : alpha(theme.palette.primary.main, 0.12),
                                        }}
                                    >
                                        <TableRow>
                                            {sortedFields.map((field, index) => (
                                                <TableCell
                                                    key={field.id}
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: (theme) =>
                                                            theme.palette.mode === 'dark'
                                                                ? theme.palette.primary.light
                                                                : theme.palette.primary.dark,
                                                        ...(index === 0 && {
                                                            position: 'sticky',
                                                            left: 0,
                                                            zIndex: 1,
                                                            bgcolor: (theme) =>
                                                                theme.palette.mode === 'dark'
                                                                    ? theme.palette.grey[800]
                                                                    : theme.palette.grey[100],
                                                            borderRight: `1px solid ${theme.palette.grey[400]}`,
                                                        }),
                                                    }}
                                                >
                                                    {field.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {innerEntries.map((entity) => (
                                            <TableRow
                                                hover
                                                key={entity.id}
                                                onDoubleClick={() =>
                                                    navigate(`/${ELinks.SALES_OPPORTUNITIES}/${entity.id}`)
                                                }
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {sortedFields.map((field, index) => {
                                                    const skillCoverage = entity.selectedEmployees.length
                                                        ? Math.round(
                                                              (entity.selectedEmployees.reduce(
                                                                  (prev, cur) => cur.skillCoverage + prev,
                                                                  0,
                                                              ) /
                                                                  entity.selectedEmployees.length) *
                                                                  100,
                                                          )
                                                        : 0;
                                                    const skillScore = entity.selectedEmployees.length
                                                        ? Math.round(
                                                              (entity.selectedEmployees.reduce(
                                                                  (prev, cur) => cur.score + prev,
                                                                  0,
                                                              ) /
                                                                  entity.selectedEmployees.length) *
                                                                  100,
                                                          )
                                                        : 0;

                                                    return (
                                                        <TableCell
                                                            key={field.id}
                                                            sx={{
                                                                ...(index === 0 && {
                                                                    position: 'sticky',
                                                                    left: 0,
                                                                    zIndex: 1,
                                                                    bgcolor: (theme) =>
                                                                        theme.palette.mode === 'dark'
                                                                            ? theme.palette.grey[900]
                                                                            : theme.palette.common.white,
                                                                    borderRight: `1px solid ${theme.palette.grey[400]}`,
                                                                }),
                                                            }}
                                                        >
                                                            {field.id === 'id' && entity.id}
                                                            {field.id === 'externalId' && entity.externalId}
                                                            {field.id === 'source' &&
                                                                (entity.sourceName && entity.sourceURL ? (
                                                                    <a
                                                                        href={entity.sourceURL}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        {entity.sourceName}
                                                                    </a>
                                                                ) : (
                                                                    entity.sourceName
                                                                ))}
                                                            {field.id === 'title' && (
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {entity.title || 'Unbekannt'}
                                                                </Typography>
                                                            )}
                                                            {field.id === 'customer' && entity.customer}
                                                            {field.id === 'employees' && entity.headCount}
                                                            {field.id === 'projectStart' &&
                                                                (entity.projectStart
                                                                    ? entity.projectStart.toLocaleDateString('de-DE')
                                                                    : '-')}
                                                            {field.id === 'projectEnd' &&
                                                                (entity.projectEnd
                                                                    ? entity.projectEnd.toLocaleDateString('de-DE')
                                                                    : '-')}
                                                            {field.id === 'publishDate' &&
                                                                (entity.publishDate
                                                                    ? entity.publishDate.toLocaleDateString('de-DE')
                                                                    : '-')}
                                                            {field.id === 'deadline' &&
                                                                (entity.deadline
                                                                    ? entity.deadline.toLocaleDateString('de-DE')
                                                                    : '-')}
                                                            {field.id === 'skillCoverage' && `${skillCoverage}%`}
                                                            {field.id === 'skillScore' && `${skillScore}%`}
                                                            {field.id === 'utilization' &&
                                                                `${Math.round(entity.utilization)}%`}
                                                            {field.id === 'remote' && `${Math.round(entity.remote)}%`}
                                                            {field.id === 'dayPrice' &&
                                                                (entity.dayPrice
                                                                    ? `${entity.dayPrice.toLocaleString('de-DE')}€`
                                                                    : '-')}
                                                            {field.id === 'status' && (
                                                                <StatusChip status={entity.status} />
                                                            )}
                                                            {field.id === 'primaryRole' &&
                                                                (entity.primaryRole ? (
                                                                    <Typography variant="body2">
                                                                        {entity.primaryRole.title}
                                                                    </Typography>
                                                                ) : (
                                                                    '-'
                                                                ))}
                                                            {field.id === 'topSkills' && (
                                                                <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                                                    {entity.skills
                                                                        .slice(0, 10)
                                                                        .map((s) => s.title)
                                                                        .join(', ')}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                );
            },
        },
    ];

    const renderOpportunityStatsChips = () => {
        const totalOpportunities = entities?.length || 0;
        const activeOpportunities = entities?.filter((opp) => statusOptionsMap.get(opp.status)?.isActive).length || 0;

        return (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                <Chip label={`${totalOpportunities} Anfragen`} color="primary" variant="outlined" size="small" />
                <Chip label={`${activeOpportunities} Aktiv`} color="success" variant="outlined" size="small" />
            </Stack>
        );
    };

    const onClickOpportunitySettings = () => {
        setSettingsDialogOpen(true);
    };

    const actions: IEntitiesPageAction[] = [
        {
            id: 'create',
            text: langStrings.newOpportunity,
            icon: <Add />,
            filter() {
                return checkPermissions('Opportunities', 'Add', permissions);
            },
            action() {
                setEditingOpportunity(undefined);
                setDialogOpen(true);
            },
        },
    ];

    const roles: IMvpRole[] = [
        {
            id: 'manager',
            label: 'Manager',
            icon: <ClipboardList size={18} />,
        },
        {
            id: 'admin',
            label: 'Admin',
            icon: <Users size={18} />,
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'create',
            label: 'Anfrage erstellen',
            icon: <ClipboardPlus size={18} />,
            onClick: () => {
                setEditingOpportunity(undefined);
                setDialogOpen(true);
            },
            variant: 'primary',
        },
        {
            id: 'settings',
            label: 'Konfigurator',
            icon: <Settings fontSize="small" />,
            onClick: onClickOpportunitySettings,
        },
    ];

    return (
        <>
            <MvpSubNav
                currentPageTitle="Anfragen"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />

            <EntitiesPage
                title={langStrings.opportunities}
                subTitle="Erstellen, verwalten und analysieren Sie Projektanfragen - mit KI-Unterstützung"
                entities={entities}
                selected={selected}
                loadingError={loadingError}
                tableFields={tableFields}
                tableId="opportunities-table-mvp"
                setSelected={setSelected}
                buttons={null}
                filter={(entity, selectedView, searchText) =>
                    selectedView.filter(entity) &&
                    (textContainsLowerCase(searchText, entity.title) ||
                        textContainsLowerCase(searchText, entity.customer) ||
                        textContainsLowerCase(searchText, entity.id.toString()))
                }
                views={[
                    {
                        id: 'all',
                        title: langStrings.oftenUsed.allEntries,
                        filter() {
                            return true;
                        },
                    },
                    {
                        id: 'active',
                        title: 'Aktive Anfragen',
                        filter(entity) {
                            return statusOptionsMap.get(entity.status)?.isActive || false;
                        },
                    },
                    {
                        id: 'my',
                        title: langStrings.oftenUsed.myEntries,
                        filter(entity) {
                            return entity.selectedEmployees.find((e) => e.id === emp?.id) !== undefined;
                        },
                    },
                ]}
                types={types}
                actions={actions}
                chips={renderOpportunityStatsChips()}
            />

            <MvpOpportunityDialog
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setEditingOpportunity(undefined);
                }}
                opportunity={editingOpportunity}
                onSave={(opportunity) => {
                    // Opportunity is already updated in Redux by the dialog
                    setDialogOpen(false);
                    setEditingOpportunity(undefined);
                }}
            />

            <MvpOpportunitySettingsDialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} />
        </>
    );
}

export default MvpOpportunitiesPage;
